import { useMemo, useState } from 'react'
import { get } from 'lodash-es'
import { createContainer } from 'unstated-next'
import { useToasts } from 'react-toast-notifications'
import clientContainer from '~/lib/client'
import { WebscriptVersion } from '../../../lib/types'
import { EventSourcePolyfill } from 'event-source-polyfill'
import {
  createFileDownload,
  normalizeForPartialMatch,
  removeEmptyProps,
} from '~/lib/util'
import useSearchQuery from '~/hooks/useSearch'
import usePagination from '~/hooks/usePagination'
import { useMutateQueryPartialKey } from '../../Common/QueryHelpers'
import { useMutation, useQuery } from '@tanstack/react-query'
import GlobalFiltersContainer from '~/hooks/useGlobalFilters'
import { FunctionType, JobEventType, JobType } from '../Common/Types'

export const WEBSCRIPTS_KEY = 'WEBSCRIPTS'

export const useWebscripts = () => {
  const { textQuery, parsedQuery } = useSearchQuery()
  const { filtersVisibility, globalFilter } =
    GlobalFiltersContainer.useContainer()

  const webscriptsPagination = usePagination({
    watch: [textQuery],
  })
  const { page, limit, setPage } = webscriptsPagination

  const opts = useMemo(() => {
    // Reset page when filter or visibility changes
    setPage(0)

    return filtersVisibility.webscripts && globalFilter
      ? { wql: globalFilter?.wql }
      : undefined
  }, [globalFilter?.wql, filtersVisibility.webscripts])

  const key = [
    WEBSCRIPTS_KEY,
    JSON.stringify({ textQuery, parsedQuery, page, limit }),
    opts,
  ]

  const { data, error, refetch } = useQuery({
    queryKey: key,
    queryFn: () =>
      fetchWebscripts({
        name: normalizeForPartialMatch(textQuery),
        page,
        showTags: 'inline',
        limit,
        tags: parsedQuery.tags,
        opts,
      }),
    gcTime: 0,
  })

  const { webscripts = [], count = 0 } = data ?? {}

  const loading = !data && !error

  const reload = () => {
    refetch()
  }

  const remove = useRemoveWebscript()

  const download = useDownload()

  return {
    loading,
    data: webscripts,
    error,
    count,
    reload,
    remove,
    download,
    webscriptsPagination,
  }
}

const fetchWebscripts = async (params: {
  name: string
  page: number
  limit: number
  showTags?: 'inline' | 'embed'
  tags?: string | undefined
  opts?: { wql?: string } | undefined
}): Promise<{ webscripts: WebscriptVersion[]; count: number }> => {
  const apiParams: any = { ...params }

  if (params.tags) {
    let tagValues: string[] = []
    if (typeof params.tags === 'string') {
      tagValues = params.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean)
    } else if (Array.isArray(params.tags)) {
      tagValues = (params.tags as any[]).flatMap(tag =>
        String(tag)
          .split(',')
          .map(t => t.trim())
          .filter(Boolean),
      )
    } else if (typeof params.tags === 'object' && params.tags !== null) {
      tagValues = Object.values(params.tags as Record<string, any>)
        .map(value => String(value))
        .filter(Boolean)
    }

    if (tagValues.length > 0) {
      apiParams.tags = tagValues
    }
  }

  const data = await clientContainer.client.registry.webscripts.list(
    removeEmptyProps(apiParams),
    params.opts,
  )
  return {
    webscripts: data.entities,
    count: data.count,
  }
}

export const useReload = () => {
  const [reload, setReload] = useState(false)
  return { reload, setReload }
}

const useRemoveWebscript = () => {
  const { addToast } = useToasts()
  const mutateQueryPartialKey = useMutateQueryPartialKey()

  return useMutation({
    mutationFn: async (arg: { webscriptName: string }) => {
      const { webscriptName } = arg
      const deleteResponse =
        await clientContainer.client.registry.webscripts.removeAll(
          webscriptName,
        )
      return { webscriptName, deleteResponse }
    },

    onSuccess: ({ webscriptName, deleteResponse }) => {
      addToast(`Deleting ${webscriptName} in process`, { appearance: 'info' })
      const eventSource = new EventSourcePolyfill(
        deleteResponse._links.event.href,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem('token')}`,
          },
          withCredentials: true,
        },
      )
      eventSource.addEventListener(JobEventType.Completed, event => {
        const eventData = JSON.parse(event.data)
        if (
          eventData.job.type === JobType.Batch &&
          eventData.function.functionType === FunctionType.WEBSCRIPT &&
          eventData.function.name === webscriptName
        ) {
          mutateQueryPartialKey(WEBSCRIPTS_KEY)
          eventSource.close()
          addToast(`Successfully deleted ${webscriptName}`, {
            appearance: 'success',
          })
        }
      })
    },
    onError: () => addToast('Something went wrong', { appearance: 'error' }),
    throwOnError: false,
  })
}

const useDownload = () => {
  const { addToast } = useToasts()

  return useMutation({
    mutationFn: async (arg: { webscriptName: string; version: string }) => {
      const { webscriptName, version } = arg
      await clientContainer.client.registry.webscripts.content(
        webscriptName,
        version,
        {},
        {
          headers: { Accept: 'application/gzip' },
        },
      )
    },
    onSuccess: result => {
      createFileDownload(`${result[0]}.gz`, result[1], 'application/gzip')
    },
    onError: error => {
      const message = get(error, 'response.data.error', error.message)
      addToast(`Failed to download: ${message}`, { appearance: 'error' })
    },
    throwOnError: false,
  })
}

export const WebscriptsContainer = createContainer(useWebscripts)
